let _overlayEl: HTMLDivElement | null = null;

const SAVING_HTML = `
  <div class="saving-overlay__panel">
    <div class="saving-overlay__indicator"></div>
    <div class="saving-overlay__content">
      <div class="saving-overlay__title">UPLOADING DATA</div>
      <div class="saving-overlay__subtitle">Library Computer Access Protocol</div>
      <div class="saving-overlay__text">
        Transmitting to central computer core&hellip;
      </div>
    </div>
    <div class="saving-overlay__indicator saving-overlay__indicator--alt"></div>
  </div>
`;

const ERROR_HTML = `
  <div class="saving-overlay__panel">
    <div class="saving-overlay__indicator saving-overlay__indicator--error"></div>
    <div class="saving-overlay__content saving-overlay__content--error">
      <div class="saving-overlay__title saving-overlay__title--error">SAVE FAILED</div>
      <div class="saving-overlay__subtitle">Unable to Reach Central Computer Core</div>
      <div class="saving-overlay__text">
        Data transmission interrupted. Verify subspace relay connection.
      </div>
      <div class="saving-overlay__actions">
        <button class="lcars-btn lcars-btn--orange" id="saving-retry-btn">Retry</button>
        <button class="lcars-btn lcars-btn--blue" id="saving-cancel-btn">Cancel</button>
      </div>
    </div>
    <div class="saving-overlay__indicator saving-overlay__indicator--error saving-overlay__indicator--alt"></div>
  </div>
`;

export function showSavingOverlay(): void {
  if (_overlayEl) return;
  _overlayEl = document.createElement("div");
  _overlayEl.className = "saving-overlay";
  _overlayEl.innerHTML = SAVING_HTML;
  document.body.appendChild(_overlayEl);
}

export function transitionToSaving(): void {
  if (!_overlayEl) return;
  _overlayEl.innerHTML = SAVING_HTML;
}

export function transitionToError(
  onRetry: () => void,
  onCancel: () => void,
): void {
  if (!_overlayEl) return;
  _overlayEl.innerHTML = ERROR_HTML;

  _overlayEl.querySelector("#saving-retry-btn")?.addEventListener("click", onRetry);
  _overlayEl.querySelector("#saving-cancel-btn")?.addEventListener("click", onCancel);
}

export function removeSavingOverlay(): void {
  if (!_overlayEl) return;
  _overlayEl.remove();
  _overlayEl = null;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Perform a save operation with the saving overlay.
 * Shows the overlay, waits for save + minimum delay, handles errors with retry/cancel.
 *
 * Returns true if save succeeded, false if user cancelled from error state.
 */
export async function performSave(
  saveFn: () => Promise<void>,
  minDelay = 1000,
): Promise<boolean> {
  showSavingOverlay();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      await Promise.all([saveFn(), delay(minDelay)]);
      removeSavingOverlay();
      return true;
    } catch {
      const choice = await new Promise<"retry" | "cancel">((resolve) => {
        transitionToError(
          () => resolve("retry"),
          () => resolve("cancel"),
        );
      });

      if (choice === "cancel") {
        removeSavingOverlay();
        return false;
      }
      // Retry — loop again
      transitionToSaving();
    }
  }
}
